import { useState, useEffect } from 'react';
import { Sparkles, X, ArrowRight, AlertCircle } from 'lucide-react';
import { logDebug } from '../lib/logger';

// Format a timestamp as a calm relative phrase. Behaviour preserved exactly.
export const formatRelativeTime = (iso) => {
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, now - then);
    const sec = Math.floor(diff / 1000);
    if (sec < 30) return 'just now';
    if (sec < 60) return `${sec}s ago`;
    const min = Math.floor(sec / 60);
    if (min < 60) return `${min} ${min === 1 ? 'minute' : 'minutes'} ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr} ${hr === 1 ? 'hour' : 'hours'} ago`;
    const day = Math.floor(hr / 24);
    if (day < 30) return `${day} ${day === 1 ? 'day' : 'days'} ago`;
    const month = Math.floor(day / 30);
    if (month < 12) return `${month} ${month === 1 ? 'month' : 'months'} ago`;
    const year = Math.floor(day / 365);
    return `${year} ${year === 1 ? 'year' : 'years'} ago`;
  } catch {
    return '';
  }
};

// Subtle personalized welcome for returning visitors (browser-storage based).
// On first visit, marks the visitor and stays silent. On any later visit, fades in.
// Includes the reflection sticky-note + past reflections list + clear-confirm flow.
export const ReturnVisitorWelcome = () => {
  const [welcome, setWelcome] = useState({ show: false, name: '' });
  const [reflectionOpen, setReflectionOpen] = useState(false);
  const [reflection, setReflection] = useState('');
  // view: 'entry' | 'saved' | 'list' | 'confirm-clear'
  const [view, setView] = useState('entry');
  const [pastList, setPastList] = useState([]);

  useEffect(() => {
    try {
      const visited = localStorage.getItem('sds_visited');
      const storedName = (localStorage.getItem('sds_visitor_name') || '').trim();
      if (visited) {
        setWelcome({ show: true, name: storedName });
      } else {
        localStorage.setItem('sds_visited', '1');
      }
    } catch (e) {
      // storage disabled — silently skip
      logDebug('localStorage read failed:', e);
    }
  }, []);

  // Close on Escape for accessibility
  useEffect(() => {
    if (!reflectionOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setReflectionOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reflectionOpen]);

  const loadPast = () => {
    try {
      const list = JSON.parse(localStorage.getItem('sds_reflections') || '[]');
      setPastList(Array.isArray(list) ? list : []);
    } catch {
      setPastList([]);
    }
  };

  const openPast = () => {
    loadPast();
    setView('list');
  };

  const closeModal = () => {
    setReflectionOpen(false);
    // reset for the next session opening
    setTimeout(() => {
      setReflection('');
      setView('entry');
      setPastList([]);
    }, 400);
  };

  const saveReflection = () => {
    const text = reflection.trim();
    if (!text) return;
    try {
      const list = JSON.parse(localStorage.getItem('sds_reflections') || '[]');
      list.unshift({ text, at: new Date().toISOString() });
      localStorage.setItem('sds_reflections', JSON.stringify(list.slice(0, 50)));
    } catch { /* storage disabled */
      logDebug('reflection save: localStorage unavailable');
    }
    setView('saved');
    setTimeout(closeModal, 1800);
  };

  const clearAll = () => {
    try { localStorage.removeItem('sds_reflections'); } catch { /* ignore */
      logDebug('reflection clear: localStorage unavailable');
    }
    setPastList([]);
    setView('list');
  };

  if (!welcome.show) return null;

  const firstName = welcome.name ? welcome.name.split(' ')[0] : '';
  const text = firstName
    ? `Welcome back, ${firstName} — the lotus is open.`
    : 'Welcome back — the lotus is open.';

  return (
    <>
      <p
        data-testid="return-visitor-welcome"
        className="welcome-whisper mt-5 text-sm md:text-base font-light italic tracking-wide text-[var(--accent-gold)]"
      >
        {text}
      </p>

      <button
        data-testid="reflection-prompt-btn"
        onClick={() => { setView('entry'); setReflectionOpen(true); }}
        className="welcome-whisper mt-2 text-[11px] md:text-xs uppercase tracking-[0.3em] text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors"
        style={{ animationDelay: '0.7s' }}
      >
        What pulled you back today?
      </button>

      {reflectionOpen && (
        <div
          data-testid="reflection-overlay"
          className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8"
        >
          <div
            className="absolute inset-0 bg-[var(--background-primary)]/80 backdrop-blur-md"
            onClick={closeModal}
            aria-hidden="true"
          />
          <div
            data-testid="reflection-card"
            className="reflection-note rounded-3xl p-6 md:p-8 max-w-md w-full relative z-10"
          >
            {view === 'saved' && (
              <div className="text-center py-4" data-testid="reflection-saved">
                <div className="icon-glow inline-flex mb-4">
                  <Sparkles className="text-[var(--accent-gold)]" size={28} />
                </div>
                <p className="font-['Cormorant_Garamond'] text-xl md:text-2xl italic text-[var(--text-accent)] glow-text-soft">
                  Your reflection has been held.
                </p>
                <p className="mt-3 text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)]">
                  Private to your device
                </p>
              </div>
            )}

            {view === 'entry' && (
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent-violet)]">
                    A quiet note
                  </span>
                  <button
                    data-testid="reflection-close-btn"
                    onClick={closeModal}
                    className="text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[var(--text-primary)] glow-text-soft mb-2 leading-tight">
                  What pulled you <span className="italic text-[var(--text-accent)]">back today?</span>
                </h3>
                <p className="text-xs text-[var(--text-muted)] mb-4 italic">
                  A few words is enough. Held only on your device.
                </p>
                <textarea
                  data-testid="reflection-textarea"
                  autoFocus
                  value={reflection}
                  onChange={(e) => setReflection(e.target.value)}
                  rows={4}
                  maxLength={500}
                  placeholder="Whisper it gently…"
                  className="w-full bg-white/5 border border-white/10 rounded-2xl px-4 py-3 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)]/60 focus:outline-none focus:border-[var(--accent-gold)]/50 transition-colors resize-none font-['Cormorant_Garamond'] text-base md:text-lg italic leading-relaxed"
                />
                <div className="flex items-center justify-between mt-4 gap-3">
                  <span className="text-[10px] text-[var(--text-muted)]/70 tracking-wide">
                    {reflection.trim().length}/500
                  </span>
                  <button
                    data-testid="reflection-save-btn"
                    onClick={saveReflection}
                    disabled={!reflection.trim()}
                    className="reveal-button px-6 py-2.5 rounded-full text-[var(--text-accent)] text-[10px] uppercase tracking-[0.3em] font-medium inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Sparkles size={12} /> Hold this
                  </button>
                </div>
                <div className="mt-5 text-center">
                  <button
                    data-testid="reflection-view-past-btn"
                    onClick={openPast}
                    className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors inline-flex items-center gap-1.5"
                  >
                    View past reflections
                    <ArrowRight size={10} />
                  </button>
                </div>
              </>
            )}

            {view === 'list' && (
              <div data-testid="reflection-list-view" className="reflection-list-fade">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[10px] uppercase tracking-[0.4em] text-[var(--accent-violet)]">
                    Past Whispers
                  </span>
                  <button
                    data-testid="reflection-list-close-btn"
                    onClick={closeModal}
                    className="text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl text-[var(--text-primary)] glow-text-soft mb-1 leading-tight">
                  Your <span className="italic text-[var(--text-accent)]">reflections</span>
                </h3>
                {pastList.length > 0 && (() => {
                  const dayKeys = new Set();
                  pastList.forEach((e) => {
                    const d = new Date(e.at);
                    if (!isNaN(d)) dayKeys.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
                  });
                  const count = pastList.length;
                  const days = dayKeys.size;
                  let label = count === 1 ? '1 reflection held' : `${count} reflections held`;
                  if (days > 1) label += ` · across ${days} days`;
                  return (
                    <p
                      data-testid="reflection-practice-indicator"
                      className="welcome-whisper text-[11px] uppercase tracking-[0.3em] text-[var(--accent-gold)]/85 mt-1 mb-3"
                      style={{ animationDelay: '0.2s' }}
                    >
                      {label}
                    </p>
                  );
                })()}
                <p className="text-xs text-[var(--text-muted)] mb-5 italic">
                  Held quietly on your device — no one else can see them.
                </p>

                {pastList.length === 0 ? (
                  <div data-testid="reflection-list-empty" className="py-10 text-center">
                    <p className="font-['Cormorant_Garamond'] text-lg italic text-[var(--text-secondary)]">
                      No reflections yet — a blank page is also sacred.
                    </p>
                  </div>
                ) : (
                  <div
                    data-testid="reflection-list-scroll"
                    className="reflection-scroll max-h-[55vh] md:max-h-[420px] overflow-y-auto pr-1 space-y-3"
                  >
                    {pastList.map((entry, i) => (
                      <div
                        key={`${entry.at}-${i}`}
                        data-testid={`reflection-entry-${i}`}
                        className="reflection-entry rounded-2xl px-4 py-3 border border-white/10 bg-white/5"
                      >
                        <p className="font-['Cormorant_Garamond'] text-base md:text-lg italic text-[var(--text-primary)] leading-relaxed whitespace-pre-wrap">
                          {entry.text}
                        </p>
                        <p className="mt-2 text-[10px] uppercase tracking-[0.3em] text-[var(--accent-gold)]/80">
                          {formatRelativeTime(entry.at)}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 flex items-center justify-between gap-3">
                  <button
                    data-testid="reflection-back-btn"
                    onClick={() => setView('entry')}
                    className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] hover:text-[var(--accent-gold)] transition-colors inline-flex items-center gap-1.5"
                  >
                    ← Add another
                  </button>
                  {pastList.length > 0 && (
                    <button
                      data-testid="reflection-clear-btn"
                      onClick={() => setView('confirm-clear')}
                      className="text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] hover:text-rose-300 transition-colors"
                    >
                      Clear all
                    </button>
                  )}
                </div>
              </div>
            )}

            {view === 'confirm-clear' && (
              <div data-testid="reflection-confirm-clear" className="text-center py-3">
                <div className="icon-glow inline-flex mb-4">
                  <AlertCircle className="text-[var(--accent-gold)]" size={26} />
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[var(--text-primary)] glow-text-soft mb-2 leading-tight">
                  Release all reflections?
                </h3>
                <p className="text-xs text-[var(--text-muted)] mb-5 italic max-w-xs mx-auto">
                  Once cleared, your past whispers cannot be retrieved. They will return to silence.
                </p>
                <div className="flex items-center justify-center gap-3">
                  <button
                    data-testid="reflection-confirm-cancel-btn"
                    onClick={() => setView('list')}
                    className="px-5 py-2 rounded-full text-[10px] uppercase tracking-[0.3em] text-[var(--text-muted)] border border-white/10 hover:text-[var(--accent-gold)] hover:border-[var(--accent-gold)]/40 transition-colors"
                  >
                    Keep them
                  </button>
                  <button
                    data-testid="reflection-confirm-clear-btn"
                    onClick={clearAll}
                    className="reveal-button px-5 py-2 rounded-full text-[10px] uppercase tracking-[0.3em] font-medium text-rose-200 border border-rose-300/30 hover:border-rose-300/60"
                  >
                    Yes, clear
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ReturnVisitorWelcome;
